import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Descriptions, Button, Space, Tag, Spin, Alert } from 'antd';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getTenant, type Tenant } from '@/api/adminApi';
import { useAuth } from '@/hooks/useAuth';

const PLAN_COLORS: Record<string, string> = {
  free: 'default',
  starter: 'blue',
  professional: 'green',
  enterprise: 'gold',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  suspended: 'orange',
  cancelled: 'red',
};

export default function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const canRead = hasPermission('admin', 'read');

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getTenant(id)
      .then((data) => setTenant(data))
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [id, t]);

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;
  if (loading) return <Spin size="large" />;
  if (error) return <Alert type="error" message={error} />;
  if (!tenant) return <Alert type="warning" message={t('app.not_found')} />;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/tenants')}>
          {t('app.back')}
        </Button>
        {hasPermission('admin', 'write') && (
          <Button icon={<EditOutlined />} onClick={() => navigate(`/admin/tenants/${id}/edit`)}>
            {t('app.edit')}
          </Button>
        )}
      </Space>

      <h2>{t('admin.tenant_detail_title')}</h2>

      <Descriptions bordered column={2}>
        <Descriptions.Item label={t('admin.field.name')}>{tenant.name}</Descriptions.Item>
        <Descriptions.Item label={t('admin.field.domain')}>{tenant.domain}</Descriptions.Item>
        <Descriptions.Item label={t('admin.field.plan')}>
          <Tag color={PLAN_COLORS[tenant.plan] ?? 'default'}>
            {t(`admin.plan_options.${tenant.plan}`, tenant.plan)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('admin.field.status')}>
          <Tag color={STATUS_COLORS[tenant.status] ?? 'default'}>
            {t(`admin.status_options.${tenant.status}`, tenant.status)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('admin.field.locale')}>{tenant.locale || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('admin.field.max_users')}>{tenant.max_users ?? '-'}</Descriptions.Item>
        <Descriptions.Item label={t('admin.field.contact_email')}>{tenant.contact_email || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('admin.field.contact_phone')}>{tenant.contact_phone || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('admin.field.address')} span={2}>{tenant.address || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('admin.field.logo_url')} span={2}>{tenant.logo_url || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('admin.field.notes')} span={2}>{tenant.notes || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('admin.field.created_at')}>
          {tenant.created_at ? new Date(tenant.created_at).toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('admin.field.updated_at')}>
          {tenant.updated_at ? new Date(tenant.updated_at).toLocaleString() : '-'}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
}
